import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewContainerRef,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { GlobalMessageService, GlobalMessageType, RoutingService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { PlpPopupService } from 'src/app/service/plp-popup/plp-popup.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { BaseSiteConfigService } from '../../../../service/base-site/base-site-config.service';
import { OrderHistoryService } from '../../../../service/orders/order-history.service';

@Component({
  selector: 'app-exploded-view-product-listing',
  templateUrl: './exploded-view-product-listing.component.html',
  styleUrls: ['./exploded-view-product-listing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ExplodedViewProductListingComponent implements OnInit, AfterViewInit, OnDestroy {
  // set default values for popover
  @Input() products$: Observable<any> = this.componentService.defaultExplodedViewProducts$.getValue();
  @Input()
  // tslint:disable-next-line: max-line-length
  productCodesWithBomItemNoMoq: any[] =
    this.componentService.defaultExplodedViewProductCodesWithBomItemNoMoq$.getValue();
  @Input() dealerPrices: DealerPrice[] = this.componentService.defaultExplodedViewDealerPrices$.getValue();
  @Input() explodedViewId: string;
  @Output() messageEvent = new EventEmitter<any>();
  @Input() showSpinnerPopover = false;
  @Input() isDiscontinuedEnabled: boolean;

  @Input() boms = this.componentService.bomExplodedView$.getValue();
  @Input() basepriceHidden: boolean;
  selectedHotspotNumbers: any[] = [];
  selectedHotspot: number;
  hotspotFromImageSubscription$: Subscription;
  selectedHotspotNumber$ = new BehaviorSubject(0);
  atLeastOneIsSelected$ = new BehaviorSubject(false);
  explodedViewFormGroup: UntypedFormGroup;
  srpSubscription$: Subscription;
  showSrpOnly = false;
  showSpinner = false;
  add2CartBtn: any;
  routerSubscription$: Subscription;
  cachedOffsetValues: any = {};
  hasDealerPrice: true;
  dealerPriceCheckerSubscription$: Subscription;

  cartAddEntrySuccessSubscription$: Subscription;
  hasCartAddEntrySuccess = false;
  cartAddEntryFail = false;

  isDesktop = false;
  cxDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  breakpointSubscription$: Subscription;
  prevBreakpoint: string;
  currBreakpoint: string;
  oddOffsetLeft: number;
  evenOffsetLeft: number;
  hotspotFirstProducts: string[];
  adjustedMarginTopValue: number;
  productData$: any;
  switcherIfProductLoaded = false;
  basepriceHiddens = false;
  isBackOrderPopupEnabled: boolean = true;
  backOrderModalSubscription: Subscription;
  initTargetElementOffsetTop: any;
  leftContentElement: any;
  activeCountry$: Observable<string> = this.countryService.getActive();

  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );

  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );
  businessType: string;

  affiliation = `/${this.currentBusinesstype.getBusinessType()}/${this.currentCountry}/${this.activeLanguage}`;

  plpPopupModalEnabled$: Subscription;
  isPlpPopupEnabled: boolean;

  constructor(
    public dealerPriceService: DealerPriceService,
    public componentService: ComponentService,
    public router: Router,
    public routingService: RoutingService,
    public cdr: ChangeDetectorRef,
    public activeCartService: ActiveCartFacade,
    public actionsSubject: ActionsSubject,
    public messageService: GlobalMessageService,
    public breakpointService: BreakpointService,
    public countryService: ShimanoCountryService,
    public checkoutServiceExtends: CheckoutServiceExtends,
    public languageService: ShimanoLanguageService,
    public customProductService: CustomProductService,
    public currentBusinesstype: CurrentBusinessType,
    public deviceDetectorService: DeviceDetectorService,
    private gtmService: GoogleTagManagerService,
    protected baseSiteConfigService: BaseSiteConfigService,
    protected orderHistoryService: OrderHistoryService,
    protected modalService: StandardModalService,
    protected el: ElementRef,
    protected vcr: ViewContainerRef,
    private storageService: StorageService,
    public plpPopupService: PlpPopupService
  ) {
    this.createExplodedViewFormGroup();
  }

  get currentCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  ngOnInit() {
    this.isBackOrderModalEnabled();
    this.checkDesktopWidth();
    this.isPlpPopupModalEnabled();
    this.boms = this.componentService.bomExplodedView$.getValue();

    // handle SRPToggle
    this.getInitSrpToggleValue();
    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrpOnly = checked;
      this.cdr.detectChanges();
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        this.clearSelectedProducts();
        if (!(this.cdr as ViewRef).destroyed) {
          this.cdr.detectChanges();
        }
      });

    // add to cart event
    this.cartAddEntrySuccessSubscription$ = this.actionsSubject
      .pipe(ofType(CartActions.CART_ADD_ENTRY_SUCCESS, CartActions.CART_ADD_ENTRY_FAIL))
      .pipe(
        map((cartAddEntryStatus: any) => {
          if (cartAddEntryStatus.type === CartActions.CART_ADD_ENTRY_SUCCESS) {
            this.hasCartAddEntrySuccess = true;
          } else {
            this.cartAddEntryFail = true;
          }
          return cartAddEntryStatus;
        }),
        concatMap(() => this.activeCartService.isStable().pipe(distinctUntilChanged())),
        tap(() => this.messageService.remove(GlobalMessageType.MSG_TYPE_CONFIRMATION))
      )
      .subscribe((isCartLoaded) => {
        if (isCartLoaded) {
          this.add2CartBtn.classList.remove('rotating', 'disabled');
          this.showSpinner = false;
          this.componentService.setMiniCartLoading(false);
          const productCodeAddedToCart = this.componentService.productCodeJustAddedToCart$.getValue();
          if (this.hasCartAddEntrySuccess && productCodeAddedToCart) {
            this.showGlobalMessage(
              'cart.addToCartSuccess',
              GlobalMessageType.MSG_TYPE_CONFIRMATION,
              productCodeAddedToCart
            );
            this.hasCartAddEntrySuccess = false;
          }
        } else {
          this.componentService.setMiniCartLoading(true);
        }
        if (!(this.cdr as ViewRef).destroyed) {
          this.cdr.detectChanges();
        }
      });

    // scrollToFirstProduct onChange of breakpoint
    this.breakpointSubscription$ = this.breakpointService.breakpoint$
      .pipe(pairwise())
      .subscribe(([prevBreakpoint, currBreakpoint]) => {
        this.prevBreakpoint = prevBreakpoint;
        this.currBreakpoint = currBreakpoint;
        if (this.componentService.atLeastOneIsSelected$.getValue() && prevBreakpoint !== currBreakpoint) {
          const selectedHotspot = this.componentService.selectedHotspotNumber$.getValue();
          this.toggleSelectedProduct(selectedHotspot, false);
          this.toggleSelectedProduct(selectedHotspot, true);
          this.scrollToFirstProduct(this.componentService.selectedHotspotNumber$.getValue(), true);
        }
      });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }
  isPlpPopupModalEnabled = () => {
    this.plpPopupModalEnabled$ = this.plpPopupService.getPlpPopupEnabledConfig$().subscribe((plpPopup) => {
      this.isPlpPopupEnabled = plpPopup.toString() === 'true';
    });
  };

  getProductBOM(code: any) {
    if (typeof this.boms !== 'undefined') {
      return this.boms
        .filter((bom: any) => bom.products.some((prod: any) => prod.code === code))
        .map((desc: any) => desc.description)[0];
    }
  }

  checkDesktopWidth() {
    this.isDesktop = window.innerWidth >= this.cxDesktopWidth;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.hotspotFromImageSubscription$ = this.componentService.selectedHotspotNumberFromImage$.subscribe(
        (hotspot) => {
          if (hotspot && !this.componentService.expandedImageClicked$.getValue()) {
            if (this.switcherIfProductLoaded) {
              this.selectedHotspot = hotspot;
              this.checkAtLeastOneIsSelected();
              this.scrollToFirstProduct(hotspot);
              this.switcherIfProductLoaded = false;
            }
          }
          if (!(this.cdr as ViewRef).destroyed) {
            this.cdr.detectChanges();
          }
        }
      );
    }, 0);
  }

  createExplodedViewFormGroup() {
    const inputGroup: any = {};
    this.productCodesWithBomItemNoMoq.forEach((p: any) => {
      if (p.productCode) {
        inputGroup[p.productCode] = new UntypedFormControl('');
      }
    });
    this.explodedViewFormGroup = new UntypedFormGroup(inputGroup);
  }

  checkInput(event: any, code: string) {
    const inputFieldValue = event.target.value;
    const minOrderQty = this.getMinOrderQuantity(code);
    const newValue = ShimanoUtilMethod.setMinimumOrderQuantity(inputFieldValue, minOrderQty);
    const checkQty = ShimanoUtilMethod.checkUserQuantity(inputFieldValue, minOrderQty);
    this.explodedViewFormGroup.controls[code].setValue(inputFieldValue > 0 ? newValue : inputFieldValue);
    if (checkQty > 0) {
      this.messageService.add(
        { key: 'moq.orderFormMessage', params: { value: newValue, moq: minOrderQty } },
        GlobalMessageType.MSG_TYPE_CONFIRMATION
      );
    }
  }

  isBackOrderModalEnabled = () => {
    this.backOrderModalSubscription = this.getBackOrderModalConfig$().subscribe((backOrder) => {
      return (this.isBackOrderPopupEnabled = backOrder === 'true');
    });
  };

  getBackOrderModalConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.backorder.popup.message'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.backorder.popup.message')
              .map((res: any) => res.value)[0]
        )
      );
  }

  backorderCartModal(cart: any = null, entries: any): void {
    this.add2CartBtn.classList.remove('rotating', 'disabled');
    this.showSpinner = false;
    this.cdr.detectChanges();

    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.BACKORDER_CART_MODAL, this.el, this.vcr, {
        backorderEntries: cart,
        formEntries: entries,
      })
      ?.pipe(take(1))
      .subscribe();

    const backOrderModal = this.modalService.getClosedModalData().subscribe((result) => {
      if (result !== undefined) {
        if (result && result !== 0 && result !== 'Cross click') {
          this.componentService.setMiniCartLoading(false);
          this.componentService.productCodeJustAddedToCart$.next(null);
          if (result !== 'hasError') {
            this.showGlobalMessage(
              'cart.addToCartSuccess',
              GlobalMessageType.MSG_TYPE_CONFIRMATION,
              entries.orderEntries[0].product.code
            );
          }
        }
        backOrderModal.unsubscribe();
      }
    });
  }

  backorderCartCheck(orderFormDetails: any) {
    this.add2CartBtn = document.querySelector('button.add-to-cart-primary.sku-' + orderFormDetails.code);
    const checkAdd2CartBtn = this.add2CartBtn.classList.contains('sku-' + orderFormDetails.code);

    if (!this.showSpinner && checkAdd2CartBtn) {
      this.add2CartBtn.classList.add('rotating', 'disabled');
      this.showSpinner = true;

      if (this.isBackOrderPopupEnabled) {
        const quantity = this.explodedViewFormGroup.get(orderFormDetails.code).value;
        const body = {
          orderEntries: [
            {
              quantity,
              product: {
                code: orderFormDetails.code,
              },
            },
          ],
        };

        const entries = this.orderHistoryService.getInventory(body);

        entries.subscribe(
          (data) => {
            if (data.inventoryBackorderList.length > 0) {
              this.backorderCartModal(data, body);
            } else {
              this.addToCart(orderFormDetails);
            }
          },
          (error) => {
            this.add2CartBtn.classList.remove('rotating', 'disabled');
            this.showSpinner = false;
            this.showGlobalMessage('cart.genericError', GlobalMessageType.MSG_TYPE_ERROR);
          }
        );
      } else {
        this.addToCart(orderFormDetails);
      }
    }
  }

  addToCart(product: any) {
    if (product) {
      const quantity = this.explodedViewFormGroup.get(product.code).value;
      if (quantity <= 0) {
        return;
      }
      this.componentService.productCodeJustAddedToCart$.next(product.code);
      this.activeCartService.addEntry(product.code, quantity);
      let categories: any = {};
      let totalValue = 0;
      totalValue = product.basePrice?.value * quantity;
      if (product.categories) {
        product.categories.forEach((cat: any, i: any) => {
          const indexCount = i + 1;
          const label = `item_category${indexCount == 1 ? '' : indexCount}`;
          categories[label] = cat.name;
        });
      }
      const gtmTag = {
        event: GoogleTagManagerEvent.ADD_TO_CART,
        ecommerce: {
          currency: this.storageService.getSessionStorageItem('currency'),
          value: totalValue.toFixed(2),
          items: [
            {
              item_id: product.code,
              item_name: product.name,
              affiliation: this.affiliation,
              item_brand: product?.brand?.name,
              ...categories,
              price: product.basePrice?.value,
              quantity: quantity,
            },
          ],
        },
      };

      if (gtmTag.ecommerce.items.length > 0) {
        this.gtmService.pushTag({
          ecommerce: null,
          search_term: '',
          results_page: '',
          affiliation: '',
          content_type: '',
          content_id: '',
          content_name: '',
        });
        this.gtmService.pushTag(gtmTag);
      }
    }
  }

  getOffsetValues() {
    const hotspotNumbers = this.productCodesWithBomItemNoMoq
      .map((p) => p.bomItemNo)
      .filter((x, i, a) => a.indexOf(x) === i);

    if (!this.cachedOffsetValues[this.explodedViewId] || this.prevBreakpoint !== this.currBreakpoint) {
      this.cachedOffsetValues[this.explodedViewId] = hotspotNumbers
        .filter((x, i, a) => a.indexOf(x) === i)
        .map(
          (hotspot) =>
            hotspot && {
              hotspot,
              offsetTop: document.getElementById(hotspot) && document.getElementById(hotspot).offsetTop,
              offsetLeft: document.getElementById(hotspot) && document.getElementById(hotspot).offsetLeft,
              firstProduct:
                document.getElementById(hotspot) &&
                this.getProductCodeFromClassName(document.getElementById(hotspot).className),
            }
        );
      if (this.prevBreakpoint !== this.currBreakpoint) this.prevBreakpoint = this.currBreakpoint;
      const offSetLeftValues: number[] = [
        ...new Set<number>(this.cachedOffsetValues[this.explodedViewId].map((o: any) => o.offsetLeft).filter(Boolean)),
      ];
      this.oddOffsetLeft = offSetLeftValues[0];
      this.evenOffsetLeft = offSetLeftValues[1];
      this.hotspotFirstProducts = this.cachedOffsetValues[this.explodedViewId]
        .map((o: any) => o.firstProduct)
        .filter(Boolean);
    }
  }

  checkAtLeastOneIsSelected() {
    if (!this.componentService.atLeastOneIsSelected$.getValue()) {
      this.componentService.atLeastOneIsSelected$.next(true);
      this.getOffsetValues();
    }
  }

  resetComponentState() {
    this.cachedOffsetValues = {};
    this.getOffsetValues();
    this.cdr.detectChanges();
  }

  toggleSelectedProduct(hotspotNumber: number, isSelected: boolean) {
    if (isSelected) {
      this.componentService.selectedHotspotNumber$.next(hotspotNumber);
      this.componentService.atLeastOneIsSelected$.next(true);
      this.cdr.detectChanges();
      // Use setTimeout to defer the scroll operation
      setTimeout(() => {
        this.scrollToFirstProduct(hotspotNumber, true);
      }, 0);
    } else {
      this.componentService.selectedHotspotNumber$.next(0);
      this.componentService.atLeastOneIsSelected$.next(false);
    }
  }

  private resizeTimeout: any;
  @HostListener('window:resize')
  onResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.resetComponentState();
      if (this.componentService.selectedHotspotNumber$.getValue()) {
        const activeHotspot = this.componentService.selectedHotspotNumber$.getValue();
        if (activeHotspot) {
          this.scrollToFirstProduct(activeHotspot, true);
        }
      }
    }, 100);
  }

  scrollToFirstProduct(hotspot: number, forceScroll = false) {
    this.leftContentElement = document.getElementById('products-container');
    const targetElement = document.getElementById(hotspot.toString());
    if (!this.leftContentElement || !targetElement) return;
    if (!this.cachedOffsetValues[this.explodedViewId] || forceScroll) {
      this.getOffsetValues();
    }
    const containerRect = this.leftContentElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    let scrollTop = this.leftContentElement.scrollTop + targetRect.top - containerRect.top;
    const offset = 1;
    scrollTop = Math.max(0, scrollTop - offset);
    const maxScroll = this.leftContentElement.scrollHeight - this.leftContentElement.clientHeight;
    scrollTop = Math.min(scrollTop, maxScroll);

    this.leftContentElement.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
    this.leftContentElement.offsetHeight;
  }

  isActive(hotspotNumber: number): boolean {
    return hotspotNumber ? this.componentService.selectedHotspotNumber$.getValue() === hotspotNumber : false;
  }

  isOutOfStock(code: string): Observable<boolean> {
    return this.products$.pipe(
      map(
        (products) =>
          products
            .filter((product: any) => product && product.code === code)
            .map((res: any) => {
              return res.stock && res.stock.stockLevelStatus === 'outOfStock'
                ? true
                : res.stock && res.stock.stockLevelStatus === 'backorder'
                ? false
                : !res.inStockFlag;
            })[0]
      )
    );
  }

  checkStockLevel(stock: number, status: string): string {
    if (status === 'discontinued') {
      return this.isDiscontinuedEnabled ? 'discontinued' : 'outOfStock';
    }
    if (status === 'backorder' && stock <= 0) {
      return 'outOfStock';
    }
    return status === 'backorder' ? 'inStock' : status;
  }

  hasReplacement(code: string): Observable<boolean> {
    return this.products$.pipe(
      map(
        (products) =>
          products
            .filter((product: any) => product && product.code === code)
            .map((res: any) => res.hasOwnProperty('replacedBy'))[0]
      )
    );
  }

  isNonSellable(code: string): Observable<boolean> {
    return this.products$.pipe(
      map(
        (products) =>
          products
            .filter((product: any) => product && product.code === code)
            .map((res: any) => {
              return (
                (res.stock && res.stock.stockLevelStatus === 'outOfStock' ? true : !res.inStockFlag) &&
                res.hasOwnProperty('replacedBy')
              );
            })[0]
      )
    );
  }

  getSrpPrice(code: string): Observable<string> {
    return this.products$.pipe(
      map(
        (products) =>
          products
            .filter((product: any) => product && product.code === code)
            .map((res: any) => res.srpPrice && res.srpPrice.formattedValue)[0]
      )
    );
  }

  getBasePrice(code: string): Observable<string> {
    return this.products$.pipe(
      map(
        (products) =>
          products
            .filter((product: any) => product && product.code === code)
            .map((res: any) => res.basePrice && res.basePrice.formattedValue)[0]
      )
    );
  }

  getMinOrderQuantity(code: string): number {
    return this.productCodesWithBomItemNoMoq
      .filter((p) => p.productCode === code)
      .map((res) => res.minOrderQuantity)[0];
  }

  getDealerPrice(productCode: string): Observable<string> {
    if (this.dealerPrices) {
      return of(this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode));
    }
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  getInitSrpToggleValue() {
    this.showSrpOnly = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  clearSelectedProducts() {
    this.componentService.selectedHotspotNumber$.next(0);
    this.componentService.atLeastOneIsSelected$.next(false);
    this.componentService.initImageContainerHeight$.next(0);
  }

  showGlobalMessage(text: string, messageType: GlobalMessageType, param?: string) {
    this.messageService.add({ key: text, params: { productName: param } }, messageType);
  }

  get isTablet$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(
      map((breakpoint) => breakpoint === BREAKPOINT.md || breakpoint === BREAKPOINT.lg)
    );
  }

  adjustMarginTop(productIndex: number, hotspot: any, elementRef: HTMLElement): boolean {
    const isProductAtEvenColumn = !!(productIndex % 2);
    elementRef.classList.remove('adjust-margin-top');

    if (
      !this.cachedOffsetValues[this.explodedViewId] ||
      !this.componentService.selectedHotspotNumber$.getValue() ||
      !this.componentService.atLeastOneIsSelected$.getValue()
    )
      return false;

    const activeElements = document.getElementsByClassName(
      'item-' + this.componentService.selectedHotspotNumber$.getValue()
    );

    const selectedHotspotFirstProductElement = activeElements[0];
    const selectedHotspotLastProductElement = activeElements[activeElements.length - 1];
    const nextToSelectedHotspotLastProductElement = selectedHotspotLastProductElement?.nextElementSibling;

    const isSelectedHotspotFirstProductAtEvenColumn = selectedHotspotFirstProductElement.className.includes('even');
    const isSelectedHotspotLastProductAtEvenColumn = selectedHotspotLastProductElement.className.includes('even');

    const productDetailsMoreElementHeight = selectedHotspotFirstProductElement
      ? (selectedHotspotFirstProductElement.getElementsByClassName('product-details-more')[0] as HTMLElement)
          .offsetHeight
      : 0;

    if (productDetailsMoreElementHeight && productDetailsMoreElementHeight > 0) {
      this.adjustedMarginTopValue = -productDetailsMoreElementHeight;
    }
    //prevent overlapping
    const containerHeight = document.getElementById('products-container').offsetHeight;
    const productTop = elementRef.offsetTop;
    if (productTop + productDetailsMoreElementHeight > containerHeight) {
      this.adjustedMarginTopValue = Math.min(
        this.adjustedMarginTopValue,
        containerHeight - (productTop + productDetailsMoreElementHeight)
      );
    }
    if (
      !nextToSelectedHotspotLastProductElement ||
      (!isSelectedHotspotFirstProductAtEvenColumn && isSelectedHotspotLastProductAtEvenColumn)
    )
      return false;

    if (hotspot == this.componentService.selectedHotspotNumber$.getValue()) {
      if (isSelectedHotspotFirstProductAtEvenColumn && !isProductAtEvenColumn) {
        elementRef.classList.add('adjust-margin-top');
      }
    }

    const onlyOneSelectedHotspot = activeElements.length === 1 ? true : false;

    // use while loop to get all elements after the selected hotspots
    var el = selectedHotspotLastProductElement;
    while ((el = el.nextElementSibling as HTMLElement)) {
      const elIsNextToLastSelectedHotspot = el === nextToSelectedHotspotLastProductElement;
      // get elements opposite of the last selected hotspot
      if (el && isSelectedHotspotLastProductAtEvenColumn !== el.className.includes('even')) {
        if (
          isSelectedHotspotLastProductAtEvenColumn || // last selected hotspot is in EVEN
          (!isSelectedHotspotLastProductAtEvenColumn && // last selected hotspot is in ODD & only 1 selected hotspot & el is not next to last selected hotspot
            onlyOneSelectedHotspot &&
            !elIsNextToLastSelectedHotspot) ||
          (!isSelectedHotspotLastProductAtEvenColumn && // 1st & last selected hotspot is in ODD & el is not next to last selected hotspot
            !isSelectedHotspotFirstProductAtEvenColumn &&
            !elIsNextToLastSelectedHotspot)
        ) {
          el.classList.add('adjust-margin-top');
        }
      }
    }
    return elementRef.className.includes('adjust-margin-top') ? true : false;
  }

  getProductCodeFromClassName(className: string): string {
    const productClassIndex = className.split(' ').indexOf('product');
    return className.split(' ').slice(productClassIndex + 1)[0];
  }

  hasProduct(product: any): boolean {
    if (product) {
      this.switcherIfProductLoaded = true;
      this.messageEvent.emit(true);
      return true;
    } else {
      this.switcherIfProductLoaded = false;
      this.messageEvent.emit(false);
      return false;
    }
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  adjustMarginBottom(productIndex: number, hotspot: number, elementRef: HTMLElement): number {
    const isProductAtEvenColumn = !!(productIndex % 2);

    if (
      !this.cachedOffsetValues[this.explodedViewId] ||
      !this.componentService.selectedHotspotNumber$.getValue() ||
      !this.componentService.atLeastOneIsSelected$.getValue()
    )
      return;

    const activeElements = document.getElementsByClassName(
      'item-' + this.componentService.selectedHotspotNumber$.getValue()
    );

    const selectedHotspotFirstProductElement = activeElements[0];
    const isSelectedHotspotFirstProductAtEvenColumn = selectedHotspotFirstProductElement.className.includes('even');

    // if product is on the same column with the first selected hotspot, apply m-b
    if (isProductAtEvenColumn === isSelectedHotspotFirstProductAtEvenColumn) {
      const productElement = elementRef.firstElementChild as HTMLElement;
      var heightDiff = 0;

      // if product is in even column
      if (isProductAtEvenColumn && elementRef.previousElementSibling) {
        const previousProductElement = elementRef.previousElementSibling.firstElementChild as HTMLElement;
        heightDiff = previousProductElement.offsetHeight - productElement.offsetHeight;
      }
      // if product is in odd column
      else if (!isProductAtEvenColumn && elementRef.nextElementSibling) {
        const nextProductElement = elementRef.nextElementSibling.firstElementChild as HTMLElement;
        heightDiff = nextProductElement.offsetHeight - productElement.offsetHeight;
      }

      if (this.isActive(hotspot)) {
        return heightDiff > 0 ? heightDiff + 16 : 16;
      } else {
        return heightDiff > 0 ? heightDiff : 0;
      }
    }
  }

  ngOnDestroy() {
    this.plpPopupModalEnabled$?.unsubscribe();
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.hotspotFromImageSubscription$) {
      this.hotspotFromImageSubscription$.unsubscribe();
    }
    if (this.breakpointSubscription$) {
      this.breakpointSubscription$.unsubscribe();
    }
    if (this.cartAddEntrySuccessSubscription$) {
      this.cartAddEntrySuccessSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
    if (this.backOrderModalSubscription) {
      this.backOrderModalSubscription.unsubscribe();
    }
  }
}
